<template>
  <div class="sync-monitor">
    <!-- Indicatore stato sincronizzazione -->
    <div class="sync-status" :class="statusClass" @click="showStatusModal = true" :title="statusTitle">
      <span class="sync-icon">{{ statusIcon }}</span>
      <span class="sync-text">{{ statusText }}</span>
    </div>

    <!-- Modale stato completo -->
    <AeModal v-model="showStatusModal" :title="t('syncMonitor.modalTitle')">
      <div class="status-content">

        <!-- Riepilogo stato -->
        <div class="status-grid">
          <div class="status-row">
            <span class="status-label">{{ t('syncMonitor.sseConnection') }}</span>
            <span class="status-value">
              <span class="dot" :class="dbStore.streamConnected ? 'dot-green' : 'dot-red'"></span>
              {{ dbStore.streamConnected ? t('syncMonitor.connected') : t('syncMonitor.disconnected') }}
            </span>
          </div>

          <div class="status-row">
            <span class="status-label">{{ t('syncMonitor.network') }}</span>
            <span class="status-value">
              <span class="dot" :class="isOnline ? 'dot-green' : 'dot-red'"></span>
              {{ isOnline ? t('syncMonitor.online') : t('syncMonitor.offline') }}
            </span>
          </div>

          <div class="status-row">
            <span class="status-label">{{ t('syncMonitor.lastDataSync') }}</span>
            <span class="status-value">{{ formatDateTime(latestFarmSync) || t('syncMonitor.never') }}</span>
          </div>

          <div class="status-row">
            <span class="status-label">{{ t('syncMonitor.lastVocabSync') }}</span>
            <span class="status-value">{{ formatDateTime(dbStore.lastVocabSync) || t('syncMonitor.never') }}</span>
          </div>

          <div class="status-row">
            <span class="status-label">{{ t('syncMonitor.queuedRecords') }}</span>
            <span class="status-value" :class="{ 'text-warning': pendingCount > 0 }">
              {{ pendingCount }}
            </span>
          </div>

          <div class="status-row" v-if="lastPushSync">
            <span class="status-label">{{ t('syncMonitor.lastPush') }}</span>
            <span class="status-value">{{ formatTime(lastPushSync) }}</span>
          </div>
        </div>

        <!-- Elenco aziende con stato sync -->
        <div v-if="farmSyncList.length > 0" class="farm-sync-section">
          <button class="section-title farm-sync-title farm-sync-toggle" @click="showFarmList = !showFarmList">
            {{ t('syncMonitor.recentFarmChanges') }}
            <span class="toggle-arrow">{{ showFarmList ? '▲' : '▼' }}</span>
          </button>
          <div v-if="showFarmList" class="farm-sync-list">
            <div
              v-for="fc in farmSyncList"
              :key="fc.id"
              class="farm-sync-item"
              :class="{ 'farm-resyncing': resyncingFarmIds.has(fc.id) }"
            >
              <span class="farm-name">{{ fc.name }}</span>
              <span class="farm-sync-age" :class="{ 'text-danger': resyncingFarmIds.has(fc.id) }">
                {{ fc.syncAge }}
              </span>
            </div>
          </div>
        </div>

        <!-- Debug: risultato checkSyncStatus (nascosto di default, toggle) -->
        <div v-if="debugResult" class="debug-section">
          <div class="section-header">
            <button class="link-button" @click="showDebugJson = !showDebugJson">
              {{ showDebugJson ? t('syncMonitor.hideJson') : t('syncMonitor.showJson') }}
            </button>
            <button class="link-button" @click="debugResult = null">{{ t('syncMonitor.close') }}</button>
          </div>
          <pre v-if="showDebugJson" class="debug-output">{{ debugResult }}</pre>
        </div>

        <!-- Errori recenti -->
        <div v-if="errors.length > 0" class="errors-section">
          <div class="section-header">
            <span class="section-title">{{ t('syncMonitor.recentErrors', { count: errors.length }) }}</span>
            <button class="link-button" @click="clearErrors">{{ t('syncMonitor.clear') }}</button>
          </div>
          <div class="error-list">
            <div v-for="(error, index) in errors" :key="index" class="error-item">
              <div class="error-header">
                <span class="error-time">{{ formatTime(error.timestamp) }}</span>
              </div>
              <div class="error-message">{{ error.message }}</div>
              <div v-if="error.details" class="error-details-text">{{ error.details }}</div>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="modal-actions">
          <AeButton
            variant="secondary"
            :disabled="resyncInProgress"
            @click="handleResyncVocab"
          >
            {{ t('syncMonitor.updateVocab') }}
          </AeButton>
          <AeButton
            variant="primary"
            :disabled="resyncInProgress"
            @click="handleForceResync"
          >
            {{ resyncInProgress ? t('syncMonitor.syncingLabel') : t('syncMonitor.syncAll') }}
          </AeButton>
        </div>
      </template>
    </AeModal>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDbStore } from '../stores/dbStore.js'
import { useAppStore } from '../stores/app.js'
import { usePoderiStore } from '../stores/poderiStore.js'
import { AeModal, AeButton } from '@aevue/components'

const { t } = useI18n()
const dbStore = useDbStore()
const appStore = useAppStore()
const poderiStore = usePoderiStore()

// State
const farmCentersCache = ref([])
const showStatusModal = ref(false)
const errors = ref([])
const syncStatus = ref('idle') // idle, syncing, checking, downloading, success, updated, error
const lastPushSync = ref(null)
const pendingCount = ref(0)
const resyncInProgress = ref(false)
const isOnline = ref(navigator.onLine)
const debugChecking = ref(false)
const debugResult = ref(null)
const resyncingFarmIds = reactive(new Set())
const showDebugJson = ref(false)
const showFarmList = ref(false)
const syncStatusDetail = ref(null)

// Aggiorna conteggio pending quando la modale si apre

// Computed
const statusClass = computed(() => {
  if (!isOnline.value) return 'status-offline'
  return `status-${syncStatus.value}`
})

const statusIcon = computed(() => {
  if (!isOnline.value) return '\u25CF' // ●
  switch (syncStatus.value) {
    case 'syncing':
    case 'checking':
    case 'downloading':
      return '\u21BB' // ↻
    case 'success':
    case 'updated':
      return '\u2713' // ✓
    case 'error':
      return '\u2715' // ✕
    default:
      return dbStore.streamConnected ? '\u25CF' : '\u25CB' // ● / ○
  }
})

// Il più recente tra tutti i last_sync_at delle aziende
const latestFarmSync = computed(() => {
  const map = dbStore.farmSyncMap || {}
  const values = Object.values(map).filter(Boolean)
  if (values.length === 0) return null
  return values.sort().pop() // ISO strings, ordinamento lessicografico = cronologico
})

// Lista aziende con nome e tempo dall'ultimo sync
const farmSyncList = computed(() => {
  const map = dbStore.farmSyncMap || {}
  const farmCenters = farmCentersCache.value
  // Usa i farm center dal poderiStore per avere i nomi; se manca, mostra l'id
  const allIds = new Set([...Object.keys(map), ...farmCenters.map(fc => fc.id_farm_center)])
  const list = []
  for (const id of allIds) {
    const fc = farmCenters.find(f => f.id_farm_center === id)
    const ts = map[id]
    list.push({
      id,
      name: fc?.name || id.substring(0, 8) + '...',
      timestamp: ts,
      syncAge: ts ? formatSyncAge(ts) : t('syncMonitor.neverSynced'),
    })
  }
  // Ordina: mai sincronizzate prima, poi per timestamp più vecchio
  list.sort((a, b) => {
    if (!a.timestamp && b.timestamp) return -1
    if (a.timestamp && !b.timestamp) return 1
    if (!a.timestamp && !b.timestamp) return 0
    return a.timestamp < b.timestamp ? -1 : 1
  })
  return list
})

function formatSyncAge(isoString) {
  try {
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now - date
    const diffSecs = Math.floor(diffMs / 1000)
    const diffMins = Math.floor(diffSecs / 60)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffSecs < 60) return t('syncMonitor.now')
    if (diffMins < 60) return t('syncMonitor.minutesAgo', { n: diffMins })
    if (diffHours < 24) return t('syncMonitor.hoursAgo', { n: diffHours })
    return t('syncMonitor.daysAgo', { n: diffDays })
  } catch {
    return isoString
  }
}

const statusText = computed(() => {
  if (!isOnline.value) return t('syncMonitor.offline')
  switch (syncStatus.value) {
    case 'syncing':
      return t('syncMonitor.syncingLabel')
    case 'checking':
      return t('syncMonitor.statusChecking')
    case 'downloading':
      return syncStatusDetail.value || t('syncMonitor.statusUpdatingDefault')
    case 'success':
      return t('syncMonitor.statusSynced')
    case 'updated':
      return syncStatusDetail.value || t('syncMonitor.statusUpdatedDefault')
    case 'error':
      return t('syncMonitor.statusError', { count: errors.value.length })
    default:
      return dbStore.streamConnected ? t('syncMonitor.online') : t('syncMonitor.statusReconnecting')
  }
})

const statusTitle = computed(() => {
  return t('syncMonitor.clickForStatus')
})

// Methods
function clearErrors() {
  errors.value = []
  if (syncStatus.value === 'error') {
    syncStatus.value = 'idle'
  }
}

function formatTime(timestamp) {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleTimeString('it-IT')
}

function formatDateTime(isoString) {
  if (!isoString) return null
  try {
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now - date
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMins = Math.floor(diffMs / (1000 * 60))

    let timeStr = date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })

    // Se è oggi, mostra solo l'ora + tempo relativo
    if (date.toDateString() === now.toDateString()) {
      if (diffMins < 1) return `${timeStr} (adesso)`
      if (diffMins < 60) return `${timeStr} (${diffMins} min fa)`
      return `${timeStr} (${diffHours}h fa)`
    }

    // Altrimenti mostra data + ora
    let dateStr = date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })
    return `${dateStr} ${timeStr} (${diffHours}h fa)`
  } catch (e) {
    return isoString
  }
}

function addError(message, details = null) {
  errors.value.push({
    timestamp: new Date(),
    message,
    details
  })
  syncStatus.value = 'error'
}

async function updatePendingCount() {
  pendingCount.value = await dbStore.getPendingSyncCount()
}

// pendingCount è mostrato solo dentro il modale (status-row "queuedRecords"):
// niente bisogno di tenerlo aggiornato in background, basta ricalcolarlo
// quando il modale viene aperto.
watch(showStatusModal, (open) => {
  if (open) updatePendingCount()
})

async function handleForceResync() {
  resyncInProgress.value = true
  try {
    await appStore.forceFullResync()
    syncStatus.value = 'success'
    setTimeout(() => {
      if (syncStatus.value === 'success') syncStatus.value = 'idle'
    }, 3000)
  } catch (e) {
    addError(t('syncMonitor.resyncError'), e.message)
  } finally {
    resyncInProgress.value = false
    await updatePendingCount()
  }
}

async function handleResyncVocab() {
  resyncInProgress.value = true
  try {
    await dbStore.resyncVocabularies()
  } catch (e) {
    addError(t('syncMonitor.vocabResyncError'), e.message)
  } finally {
    resyncInProgress.value = false
  }
}

async function handleDebugCheck() {
  debugChecking.value = true
  debugResult.value = null
  resyncingFarmIds.clear()
  syncStatus.value = 'checking'
  syncStatusDetail.value = null
  try {
    // Chiama il vero checkSyncStatus — controlla E risincronizza se serve
    const result = await dbStore.checkSyncStatus()
    // Evidenzia le aziende che sono state riscaricate
    if (result.farmCenters?.length) {
      const n = result.farmCenters.length
      const farms = n === 1 ? t('syncMonitor.farmSingular') : t('syncMonitor.farmPlural')
      syncStatus.value = 'downloading'
      syncStatusDetail.value = t('syncMonitor.updatingFarms', { n, farms })
      for (const id of result.farmCenters) {
        resyncingFarmIds.add(id)
      }

      // Mostra "Aggiornato" dopo il resync
      syncStatus.value = 'updated'
      syncStatusDetail.value = t('syncMonitor.updatedFarms', { n, farms })
      // Torna a idle dopo 5 secondi
      setTimeout(() => {
        if (syncStatus.value === 'updated') {
          syncStatus.value = 'idle'
          syncStatusDetail.value = null
        }
        resyncingFarmIds.clear()
      }, 5000)
    } else if (result.vocabRefreshed) {
      syncStatus.value = 'updated'
      syncStatusDetail.value = t('syncMonitor.vocabUpdated')
      setTimeout(() => {
        if (syncStatus.value === 'updated') {
          syncStatus.value = 'idle'
          syncStatusDetail.value = null
        }
      }, 5000)
    } else {
      // Nessuna novità
      syncStatus.value = 'success'
      syncStatusDetail.value = null
      setTimeout(() => {
        if (syncStatus.value === 'success') syncStatus.value = 'idle'
      }, 3000)
    }
    debugResult.value = JSON.stringify({
      localMap: dbStore.farmSyncMap,
      result
    }, null, 2)
  } catch (e) {
    debugResult.value = JSON.stringify({ error: e.message }, null, 2)
    addError(t('syncMonitor.checkError'), e.message)
  } finally {
    debugChecking.value = false
    await updatePendingCount()
  }
}

// Online/offline tracking
function onOnlineChange() {
  isOnline.value = navigator.onLine
}

// Monitora le chiamate syncToServer wrappandole
onMounted(async () => {
  window.addEventListener('online', onOnlineChange)
  window.addEventListener('offline', onOnlineChange)

  try { farmCentersCache.value = await poderiStore.fetchFarmCenters() } catch (e) { /* ignore */ }

  // [TEMP DEBUG - indagine rallentamento] dbStore è un singleton Pinia: se questo
  // componente viene montato più volte nella stessa sessione (es. navigando tra le
  // view del modulo Zoo, che incorporano ciascuna un proprio <PoderiLayout>/<SyncMonitor>),
  // wrappare dbStore.syncToServer ad ogni mount senza mai ripristinarlo in onUnmounted
  // accumula un livello di wrapping per ogni navigazione. Ogni sync finisce così per
  // eseguire N scansioni sequenziali di updatePendingCount() (una per livello), che
  // peggiora progressivamente nella sessione ed è risolto solo da un reload (che
  // azzera il Pinia store). Il flag sotto rende il wrapping idempotente: avviene una
  // sola volta per sessione, indipendentemente da quante volte il componente monta.
  if (!dbStore.__syncMonitorWrapped) {
  dbStore.__syncMonitorWrapped = true
  const originalSyncToServer = dbStore.syncToServer

  if (originalSyncToServer) {
    dbStore.syncToServer = async (...args) => {
      syncStatus.value = 'syncing'

      try {
        const result = await originalSyncToServer.apply(dbStore, args)

        if (result && !result.ok) {
          const mainMessage = result.message || t('syncMonitor.syncFailed')
          if (result.errors && Array.isArray(result.errors) && result.errors.length > 0) {
            result.errors.forEach(err => {
              addError(
                mainMessage,
                typeof err === 'string' ? err : (err.message || JSON.stringify(err))
              )
            })
          } else {
            addError(t('syncMonitor.syncError'), mainMessage)
          }
        } else if (result && result.skipped) {
          // Sync saltata, non cambiare stato
        } else {
          syncStatus.value = 'success'
          lastPushSync.value = new Date()
          // Ricalcola solo se il modale è aperto: se è chiuso, pendingCount non è
          // visibile da nessuna parte e si aggiornerà comunque alla prossima apertura.
          if (showStatusModal.value) await updatePendingCount()
          setTimeout(() => {
            if (syncStatus.value === 'success') syncStatus.value = 'idle'
          }, 3000)
        }

        return result
      } catch (error) {
        addError(t('syncMonitor.syncError'), error.message || String(error))
        throw error
      }
    }
  }
  } // [/TEMP DEBUG] fine guard __syncMonitorWrapped
})

onUnmounted(() => {
  window.removeEventListener('online', onOnlineChange)
  window.removeEventListener('offline', onOnlineChange)
})
</script>

<style scoped>
.sync-monitor {
  position: relative;
}

.sync-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius, 8px);
  background: var(--card-bg, rgba(255, 255, 255, 0.7));
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  user-select: none;
}

.sync-status:hover {
  background: var(--button-hover-bg, rgba(255, 255, 255, 0.85));
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.sync-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.sync-text {
  font-weight: 500;
  white-space: nowrap;
}

/* Status colors */
.status-idle .sync-icon {
  color: #4caf50;
}

.status-syncing .sync-icon,
.status-checking .sync-icon,
.status-downloading .sync-icon {
  color: #2196f3;
  animation: rotate 1s linear infinite;
}

.status-downloading {
  background: rgba(33, 150, 243, 0.1);
  border-color: rgba(33, 150, 243, 0.3);
}

.status-downloading .sync-text {
  color: #1565c0;
}

.status-updated {
  background: rgba(76, 175, 80, 0.15);
  border-color: rgba(76, 175, 80, 0.4);
}

.status-updated .sync-icon {
  color: #2e7d32;
}

.status-updated .sync-text {
  color: #2e7d32;
  font-weight: 600;
}

.status-success {
  background: rgba(76, 175, 80, 0.1);
  border-color: rgba(76, 175, 80, 0.3);
}

.status-success .sync-icon {
  color: #4caf50;
}

.status-success .sync-text {
  color: #2e7d32;
}

.status-error {
  background: rgba(244, 67, 54, 0.1);
  border-color: rgba(244, 67, 54, 0.3);
}

.status-error .sync-icon {
  color: #f44336;
}

.status-error .sync-text {
  color: #c62828;
}

.status-offline {
  background: rgba(198, 40, 40, 0.1);
  border-color: rgba(198, 40, 40, 0.3);
}

.status-offline .sync-icon {
  color: #e53935;
}

.status-offline .sync-text {
  color: #c62828;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Modal content */
.status-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.status-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.status-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--card-bg, rgba(255, 255, 255, 0.5));
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.06));
  border-radius: 6px;
}

.status-label {
  font-size: 0.9rem;
  color: var(--text-muted, #718096);
}

.status-value {
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.text-warning {
  color: #ff9800;
  font-weight: 600;
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.dot-green { background: #4caf50; }
.dot-red { background: #f44336; }

/* Errors section */
.errors-section {
  border-top: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
  padding-top: 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.section-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #c62828;
}

.link-button {
  background: none;
  border: none;
  color: var(--color-primary, #2196f3);
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
  text-decoration: underline;
}

.link-button:hover {
  opacity: 0.8;
}

.error-list {
  max-height: 250px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.error-item {
  padding: 0.75rem;
  background: rgba(244, 67, 54, 0.05);
  border: 1px solid rgba(244, 67, 54, 0.15);
  border-radius: 6px;
}

.error-header {
  margin-bottom: 0.25rem;
}

.error-time {
  font-size: 0.8rem;
  color: #999;
}

.error-message {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-color, #333);
}

.error-details-text {
  font-size: 0.8rem;
  color: #666;
  font-family: monospace;
  background: rgba(0, 0, 0, 0.04);
  padding: 0.4rem;
  border-radius: 4px;
  margin-top: 0.25rem;
  white-space: pre-wrap;
  word-break: break-word;
}

/* Farm sync list */
.farm-sync-section {
  border-top: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
  padding-top: 1rem;
}

.farm-sync-title {
  color: var(--text-color, #333);
  margin-bottom: 0.5rem;
}

.farm-sync-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  font-size: inherit;
  font-weight: 600;
  padding: 0.25rem 0;
  text-align: left;
}

.toggle-arrow {
  font-size: 0.75rem;
  color: var(--text-muted, #888);
}

.farm-sync-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.farm-sync-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: var(--card-bg, rgba(255, 255, 255, 0.5));
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.06));
  border-radius: 6px;
  transition: all 0.3s ease;
}

.farm-sync-item.farm-resyncing {
  background: rgba(244, 67, 54, 0.08);
  border-color: rgba(244, 67, 54, 0.3);
}

.farm-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-color, #333);
}

.farm-sync-age {
  font-size: 0.85rem;
  color: var(--text-muted, #718096);
}

.text-danger {
  color: #c62828;
  font-weight: 600;
}

/* Debug section */
.debug-section {
  border-top: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
  padding-top: 1rem;
}

.debug-title {
  color: #1565c0;
}

.debug-output {
  font-size: 0.8rem;
  font-family: monospace;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.1));
  border-radius: 6px;
  padding: 0.75rem;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .sync-status {
    padding: 0.4rem 0.8rem;
    font-size: 0.85rem;
  }

  .sync-text {
    display: none;
  }

  .sync-icon {
    font-size: 1.4rem;
  }
}
</style>
